import { useCallback } from "react";
import { api } from "../utils/apiHandler";
import type { AxiosResponse } from "axios";

export interface Order {
  id: string;
  order_id: string;
  amount_cents: number;
  currency: string;
  status: string;
  payment_session_id?: string;
}

export interface PackageInfo {
  id: string;
  name: string;
  credits: number;
  amount_cents: number;
  currency: string;
}

export interface CreateOrderRequest {
  package_id: string;
}

export interface CreateOrderResponse {
  order: Order;
  package: PackageInfo;
}

export interface VerifyOrderRequest {
  // Stripe
  session_id?: string;
  // CashFree
  payment_session_id?: string;
  order_id?: string;
  // Razorpay
  razorpay_payment_id?: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
}

export interface VerifyOrderResponse {
  order: {
    id: string;
    order_id: string;
    amount: number;
    currency: string;
    status: string;
  };
  user: {
    id: string;
    credits: number;
  };
}

const ORDERS_PATH = "/orders";

const useOrdersResource = () => {
  // Create order
  const createOrder = useCallback(
    async (params: CreateOrderRequest): Promise<CreateOrderResponse> => {
      const response: AxiosResponse<CreateOrderResponse> = await api.request<
        CreateOrderRequest,
        AxiosResponse<CreateOrderResponse>
      >({
        method: "POST",
        url: ORDERS_PATH,
        data: params,
      });
      return response.data;
    },
    []
  );

  // Verify payment
  const verifyOrder = useCallback(
    async (
      id: string,
      params: VerifyOrderRequest
    ): Promise<VerifyOrderResponse> => {
      const response: AxiosResponse<VerifyOrderResponse> = await api.request<
        VerifyOrderRequest,
        AxiosResponse<VerifyOrderResponse>
      >({
        method: "PATCH",
        url: `${ORDERS_PATH}/${id}/verify`,
        data: params,
      });
      return response.data;
    },
    []
  );

  return { createOrder, verifyOrder };
};

export default useOrdersResource;
